import { cloneDeep } from 'lodash';
import { mergeState } from './mergeState';

export const MigrationErrorName = 'MigrationError';
export class MigrationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = MigrationErrorName;
  }
}

export const loadAndMigrateState = (
  currentState: AbstractState,
  loadedState: AbstractState,
  migrators: Migrator[],
  beibehalten?: boolean,
  additionalData?: unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const newState = cloneDeep(currentState);

  const currentVersion = currentState.version;
  const migratedState = migrateState(
    loadedState,
    currentVersion,
    migrators,
    currentState,
    beibehalten,
    additionalData,
  );

  const result = mergeState(newState, migratedState);

  return { ...result };
};

const migrateState = (
  fromState: AbstractState,
  toVersion: number,
  migrators: Migrator[],
  context?: AbstractState,
  beibehalten?: boolean,
  additionalData?: unknown,
): AbstractState => {
  const fromVersion = fromState.version;
  validateRange(fromVersion, toVersion);
  const migratorsToApply = getMigratorsForVersionRange(migrators, fromVersion, toVersion);
  validateMigrators(migratorsToApply);
  return migratorsToApply.reduce((previousState, migrator) => {
    validateMigratorSequence(previousState.version, migrator.toVersion);
    try {
      return migrator.migratorFunction(
        previousState,
        migrator.toVersion,
        context,
        beibehalten,
        additionalData,
      );
    } catch (e) {
      throw new MigrationError(
        `Migratorfunktion: von State '${previousState.version}' zu '${migrator.toVersion}' fehlgeschlagen. ErrorMessage: ${(e as Error).message}`,
      );
    }
  }, fromState);
};

const getMigratorsForVersionRange = (
  migrators: Migrator[],
  fromVersion: number,
  toVersion: number,
): Migrator[] => {
  migrators.sort((migratorA, migratorB) => migratorA.toVersion - migratorB.toVersion);
  return migrators.filter(
    stateMigrator => stateMigrator.toVersion > fromVersion && stateMigrator.toVersion <= toVersion,
  );
};

const validateRange = (fromVersion: number, toVersion: number): void => {
  const defaultRangeErrorText = generateDefaultErrorText(fromVersion, toVersion);
  if (fromVersion == null) {
    throw new MigrationError(defaultRangeErrorText + 'Ursprungs-Version darf nicht null sein!');
  }
  if (toVersion == null) {
    throw new MigrationError(defaultRangeErrorText + 'Ziel-Version darf nicht null sein!');
  }
  if (toVersion < fromVersion) {
    throw new MigrationError(
      defaultRangeErrorText + 'Ziel-Version darf nicht kleiner als Ursprungs-Version sein!',
    );
  }
};

const validateMigrators = (migrators: Migrator[]): void => {
  const migratorTargetVersions = migrators.map(migrator => migrator.toVersion);
  const versionSet = new Set(migratorTargetVersions);

  if (versionSet.size !== migratorTargetVersions.length) {
    throw new MigrationError(
      'Migrator für eine Version mehrfach vorhanden: Es darf nur genau ein Migrator für eine Version existieren!',
    );
  }
};

const validateMigratorSequence = (fromVersion: number, toVersion: number): void => {
  if (fromVersion + 1 !== toVersion) {
    throw new MigrationError(
      `${generateDefaultErrorText(fromVersion, toVersion)}` +
        `Versionssprung von Ursprungs-Version "${fromVersion}" zu Ziel-Version "${toVersion}" muss genau 1 sein. ` +
        `Ist aber "${toVersion - fromVersion}"`,
    );
  }
};

const generateDefaultErrorText = (fromVersion: number, toVersion: number): string =>
  `AppState Migration von Ursprungs-Version ${fromVersion} zu Ziel-Version ${toVersion} nicht möglich. \n Details: `;

export interface Migrator<S extends AbstractState = AbstractState> {
  toVersion: number;
  migratorFunction(
    previousState: S,
    newVersion: number,
    context?: AbstractState,
    beibehalten?: boolean,
    additionalData?: unknown,
  ): AbstractState;
}

export interface AbstractState {
  version: number;
}
